import Row from "@components/layout/Row"
import React, { useState, useMemo, useCallback } from "react"
import styled from "styled-components"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`

const SortControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 768px) {
    /* flex-direction: column; */
  }
`

const SortSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.secondary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  background-color: ${({ theme }) => theme.white};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.primaryText};
`

const SortToggle = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.primary};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.primary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
`

const SearchInput = styled.input`
  padding: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.secondary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  width: 100%;
  margin-top: 2rem;
  height: 40px;
  min-width: 700px;

  outline: none;

  @media (max-width: 768px) {
    min-width: 90vw;
    width: 100%;
  }
`

export interface FilterAndSortListPropertiesProps<T extends { id: string }> {
  items: T[]
  renderItem: (item: T) => React.ReactNode
  searchFields: (keyof T)[]
  sortOptions: { value: keyof T; label: string }[]
  defaultSort: keyof T
  filterFn?: (item: T, search: string) => boolean
  addButton?: React.ReactNode
}

function FilterAndSortListProperties<T extends { id: string }>({
  items,
  renderItem,
  searchFields,
  sortOptions,
  defaultSort,
  filterFn,
  addButton,
}: FilterAndSortListPropertiesProps<T>) {
  // Basic state for search and sort
  const [searchTerm, setSearchTerm] = useState("")
  const [sortCriteria, setSortCriteria] = useState<keyof T>(defaultSort)
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc")

  // Wrap our default filter function so its reference is stable.
  const defaultFilterFn = useCallback(
    (item: T, search: string) => {
      const lowerSearch = search.toLowerCase()
      return searchFields.some((field) => {
        const value = item[field]
        return typeof value === "string" && value.toLowerCase().includes(lowerSearch)
      })
    },
    [searchFields],
  )

  const appliedFilterFn = filterFn ? filterFn : defaultFilterFn

  const filteredItems = useMemo(() => {
    return items.filter((item) => appliedFilterFn(item, searchTerm))
  }, [items, searchTerm, appliedFilterFn])

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems].sort((a, b) => {
      const aVal = a[sortCriteria]
      const bVal = b[sortCriteria]
      if (typeof aVal === "string" && typeof bVal === "string") {
        return aVal.localeCompare(bVal)
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return aVal - bVal
      }
      return 0
    })
    return sortOrder === "asc" ? sorted : sorted.reverse()
  }, [filteredItems, sortCriteria, sortOrder])

  return (
    <Container>
      <SearchInput placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

      <Row justify="space-between">
        <SortControls>
          <div>
            <label>
              Trier par:{" "}
              <SortSelect value={String(sortCriteria)} onChange={(e) => setSortCriteria(e.target.value as keyof T)} aria-label="Trier par">
                {sortOptions.map((option) => (
                  <option key={option.value as string} value={option.value as string}>
                    {option.label}
                  </option>
                ))}
              </SortSelect>
            </label>
          </div>
          <SortToggle onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}>{sortOrder === "asc" ? "Asc" : "Desc"}</SortToggle>
        </SortControls>
      </Row>
      {addButton}

      {sortedItems.map((item) => (
        <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
      ))}
    </Container>
  )
}

export default FilterAndSortListProperties
